import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fade,
  TextField,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../store/Index";
import {
  setSelectedBook,
  updateScannedComments,
} from "../../../../../store/slice/MemoryBookSlice";
import { updateMemoryBookAssignment } from "./service/memoryBook.api";

interface MemoryBookUpdateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const MemoryBookUpdateModal: React.FC<MemoryBookUpdateModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const { selectedBook } = useSelector((state: RootState) => state.memoryBook);

  const [scannedInput, setScannedInput] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const scanned = parseInt(scannedInput, 10);

    if (!selectedBook || isNaN(scanned) || scanned <= 0) {
      return;
    }

    if (scanned > selectedBook.unfinished_comments) {
      setError(
        `Cannot scan more than ${selectedBook.unfinished_comments} remaining comments`,
      );
      return;
    }

    if (!selectedBook.assignmentId) {
      setError("Assignment not found. Please try again.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const currentDone =
        selectedBook.total_comments - selectedBook.unfinished_comments;
      const newDone = currentDone + scanned;

      await updateMemoryBookAssignment(selectedBook.assignmentId, {
        done_comments: newDone,
      });

      dispatch(updateScannedComments({ scanned }));

      const newUnfinished = selectedBook.unfinished_comments - scanned;
      dispatch(
        setSelectedBook({
          ...selectedBook,
          unfinished_comments: newUnfinished,
          status: newUnfinished === 0 ? "completed" : "in_progress",
        }),
      );

      setScannedInput("");
      setError(null);

      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (err) {
      console.error("Failed to update scanned comments:", err);
      setError("Failed to update progress. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isSubmitting) return;

    onClose();
    setScannedInput("");
    setError(null);
  };

  const scanned = parseInt(scannedInput, 10) || 0;
  const inputError =
    !!scannedInput &&
    (!selectedBook ||
      isNaN(scanned) ||
      scanned <= 0 ||
      scanned > selectedBook.unfinished_comments);

  return (
    <Dialog
      open={open}
      TransitionComponent={Fade}
      keepMounted
      onClose={handleCancel}
      PaperProps={{
        sx: {
          width: "75vw",
          height: "75vh",
          maxWidth: "900px",
          borderRadius: 2,
          p: 0,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(20px)",
        },
      }}
    >
      <Box sx={{ p: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1,
              background: `linear-gradient(135deg, ${
                reduxTheme.primary_colors?.[400] || "#614438"
              } 0%, ${reduxTheme.primary_colors?.[300] || "#C29363"} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Update Progress
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 3, color: reduxTheme.neutral_colors?.[500] }}
          >
            How many comments have you scanned?
          </Typography>

          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: reduxTheme.primary_colors?.[900],
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Memory Book for{" "}
              <strong>{selectedBook?.title || "Not selected"}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Remaining:{" "}
              <strong>{selectedBook?.unfinished_comments || 0} comments</strong>
            </Typography>
          </Box>

          <TextField
            fullWidth
            type="number"
            label="Comments scanned"
            value={scannedInput}
            onChange={(e) => {
              setScannedInput(e.target.value);
              setError(null);
            }}
            placeholder="Enter number of comments"
            autoFocus
            error={inputError || !!error}
            helperText={
              error ||
              (inputError && selectedBook
                ? `Enter a number between 1 and ${selectedBook.unfinished_comments}`
                : "")
            }
            disabled={isSubmitting}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "& fieldset": {
                  borderColor:
                    reduxTheme.neutral_colors?.[300] || "rgba(0,0,0,0.1)",
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                },
                "&.Mui-focused fieldset": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                },
              },
            }}
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSubmitting}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                borderColor: reduxTheme.neutral_colors?.[300] || "#e0e0e0",
                color: reduxTheme.neutral_colors?.[100] || "#333",
                "&:hover": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                  transform: "scale(1.02)",
                },
                "&:disabled": {
                  opacity: 0.6,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !scannedInput ||
                isNaN(parseInt(scannedInput, 10)) ||
                parseInt(scannedInput, 10) <= 0 ||
                parseInt(scannedInput, 10) >
                  (selectedBook?.unfinished_comments ?? 0)
              }
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                background: `linear-gradient(135deg, ${
                  reduxTheme.primary_colors?.[500] || "#503629"
                } 0%, ${reduxTheme.primary_colors?.[400] || "#614438"} 100%)`,
                "&:hover": {
                  transform: isSubmitting ? "none" : "scale(1.02)",
                },
                "&:disabled": {
                  background: `rgba(${
                    reduxTheme.primary_colors?.[400]
                      ? parseInt(reduxTheme.primary_colors[400].slice(1, 3), 16)
                      : 97
                  }, ${
                    reduxTheme.primary_colors?.[400]
                      ? parseInt(reduxTheme.primary_colors[400].slice(3, 5), 16)
                      : 68
                  }, ${
                    reduxTheme.primary_colors?.[400]
                      ? parseInt(reduxTheme.primary_colors[400].slice(5, 7), 16)
                      : 56
                  }, 0.3)`,
                },
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </Box>
        </motion.div>
      </Box>
    </Dialog>
  );
};

export default MemoryBookUpdateModal;